import useFilterHook from "hooks/useFilter";
import usePagination from "hooks/usePagination";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  TEST_RATE_COLUMNS_DATA,
  FILTER_DEFINITIONS,
  INVESTIGATION_MANAGER_COLUMNS_DATA,
} from "./config";
import { DEFAULT_TOTAL_RECORDS, MODALS, STALE_TIME } from "utils/constants";
import FilterPanelMaster from "./FilterPanelMaster";
import Table from "components/Common/Table/index";
import { useMutation } from "@tanstack/react-query";
import {
  fetchLimsFranchiseCopyRates,
  fetchPanelMaster,
  franchiseInvestigationDetails,
  updateLimsFranchiseCopyRates,
  updatePanelMaster,
  updatePanelMasterSync,
} from "services/panelMaster";
import { useAlert } from "context/alert";
import Loader from "components/Common/loader";
import { FaSyncAlt } from "react-icons/fa";
import zoneLocalStorageId from "utils/zone";
import { FaDownload, FaFilePdf } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { use } from "react";
import Modal from "components/Common/Modal";
import Details from "./Details";
import { fetchInvestigationManager } from "services/investigationManager";

const PanelMaster = () => {
  const { showAlert } = useAlert();

  const hasAppliedPercentageMap = useRef({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);
  const [editableRows, setEditableRows] = useState({});
  const [rate, setRate] = useState("");
  const [mrp, setMrp] = useState("");
  const [ratePercentage, setRatePercentage] = useState("");
  const [mrpPercentage, setMrpPercentage] = useState("");
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [previousData, setPreviousData] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [error, setError] = useState(false);
  const [selectedFranchiseId, setSelectedFranchiseId] = useState(null);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState(null);

  const [transferRateToAllFranchise, setTransferRateToAllFranchise] =
    useState(false);

  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise"))
  );
  const [isFranchise] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee"))
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise"))
  );

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINITIONS,
    updateCurrentPage
  );

  const filterRef = useRef(filters);

  const hasRateFilters =
    filters.rate ||
    filters.mrp_rate ||
    filters.rate_percentage ||
    filters.mrp_percentage;

  const shouldShowTable =
    (filters.is_active || filters.is_active === false) &&
    (filters.billing_category !== "" ||
      filters.investigation_id ||
      filters.lab_department_id ||
      filters.franchise_id ||
      filters.zone_id ||
      filters.test_name);

  const {
    data: testRateData = [],
    isLoading,
    isFetching,
    refetch: refetchTestRate,
  } = useQuery(
    ["fetch-PanelMaster", currentPage, filters],
    () => fetchLimsFranchiseCopyRates(currentPage, filters),
    {
      keepPreviousData: true,
      staleTime: 5000,
      onSuccess: (res) => {
        updateTotalRecords(res.data.additional_data.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        const defaults = res?.data?.default;

        if (response?.length && defaults?.length > 0) {
          const defaultRatesMap = defaults.reduce((acc, item) => {
            acc[item.investigation_id] = item.rate;
            return acc;
          }, {});
          res.data.data = response.map((item, index) => ({
            ...item,
            rates: defaultRatesMap[item.investigation_id] || 0,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        } else {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching TestRates List:", res);
      },
      enabled: !!shouldShowTable && !hasRateFilters,
    }
  );

  const {
    data: franchiseInvestigationDetailsData = [],
    refetch: refetchFranchiseInvestigationDetails,
  } = useQuery(
    [
      "fetch-franchise-investigation-details",
      selectedFranchiseId,
      selectedInvestigationId,
    ],
    () =>
      franchiseInvestigationDetails(
        selectedFranchiseId,
        selectedInvestigationId
      ),
    {
      keepPreviousData: true,
      staleTime: 5000,
      onSuccess: (res) => {
        // updateTotalRecords(res.data.additional_data.total_count);
      },
      select: (res) => {
        const response = res?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching TestRates List:", res);
      },
      enabled: selectedFranchiseId !== null && selectedInvestigationId !== null,
    }
  );

  console.log(
    "franchiseInvestigationDetailsData",
    franchiseInvestigationDetailsData?.data
  );

  useEffect(() => {
    // console.log("first useEffect");
    if (
      !isFetchingData &&
      shouldShowTable &&
      !hasRateFilters &&
      filterRef.current !== filters
    ) {
      setIsFetchingData(true);
      refetchTestRate().finally(() => {
        setIsFetchingData(false);
        filterRef.current = filters;
      });
    }
  }, [
    filters,
    shouldShowTable,
    hasRateFilters,
    refetchTestRate,
    isFetchingData,
    currentPage,
  ]);

  const onSuccess = () => {
    const message = "Department updated successfully!";
    showAlert(message, "success");
    updateFilter("investigation_id", "");

    // Reset everything completely
    setEnableSorting(false);
    setSelectedRows([]);
    setPreviousData([]);

    // Refetch data
    refetchTestRate();

    // Re-enable sorting after data is loaded
    setTimeout(() => {
      setEnableSorting(true);
    }, 1000);
  };

  const onError = useCallback(() => {
    showAlert("Something went wrong!", "danger");
    setEnableSorting(true);
  }, [showAlert]);

  const { mutate: updatePanelMasterMutate, isLoading: isSaving } = useMutation({
    mutationFn: updateLimsFranchiseCopyRates,
    onSuccess,
    onError,
  });

  useEffect(() => {
    // console.log("second useEffect");
    selectedRows.forEach((id) => {
      hasAppliedPercentageMap.current[id] = false;
    });
  }, [ratePercentage]);

  const [enableSorting, setEnableSorting] = useState(true);

  const sortedData = testRateData?.data?.data?.length
    ? enableSorting && selectedRows.length > 0
      ? testRateData.data.data.sort((a, b) => {
          const isSelectedA = selectedRows.includes(a.id);
          const isSelectedB = selectedRows.includes(b.id);

          if (isSelectedA && !isSelectedB) return -1;
          if (!isSelectedA && isSelectedB) return 1;
          return 0;
        })
      : [...testRateData.data.data]
    : [];

  const mergedData = [
    ...new Map(
      [...previousData, ...sortedData].map((item) => [item.id, item])
    ).values(),
  ];

  const toggleSelectAll = useCallback(
    (isChecked) => {
      const visibleRowIds = sortedData.map((row) => row.id);

      if (isChecked) {
        setSelectedRows(visibleRowIds);

        const updatedSelectedData = testRateData?.data?.data
          ? testRateData.data.data.filter((row) =>
              visibleRowIds.includes(row.id)
            )
          : [];

        setPreviousData((prev) => {
          const mergedData = [
            ...new Map(
              [...prev, ...updatedSelectedData].map((item) => [item.id, item])
            ).values(),
          ];
          return mergedData;
        });

        const allEditableRows = {};
        visibleRowIds.forEach((id) => {
          allEditableRows[id] = true;
          hasAppliedPercentageMap.current[id] = false;
        });
        setEditableRows(allEditableRows);
      } else {
        setSelectedRows([]);
        setEditableRows({});
        hasAppliedPercentageMap.current = {};
      }
    },
    [sortedData, testRateData]
  );

  const toggleSelectRow = useCallback(
    (id) => {
      setSelectedRows((prevSelected) => {
        const updatedSelected = prevSelected.includes(id)
          ? prevSelected.filter((rowId) => rowId !== id)
          : [...prevSelected, id];

        if (updatedSelected.includes(id)) {
          hasAppliedPercentageMap.current[id] = false;
        } else {
          delete hasAppliedPercentageMap.current[id];
        }

        const updatedSelectedData = testRateData?.data
          ? testRateData.data.data.filter((row) =>
              updatedSelected.includes(row.id)
            )
          : [];
        setPreviousData((prev) => {
          const mergedData = [
            ...new Map(
              [...prev, ...updatedSelectedData].map((item) => [item.id, item])
            ).values(),
          ];
          return mergedData;
        });

        return updatedSelected;
      });

      setEditableRows((prevEditable) => ({
        ...prevEditable,
        [id]: !prevEditable[id],
      }));
    },
    [testRateData]
  );

  const localRateMap = useRef({});
  const localRateMrpMap = useRef({});
  const manuallyEditedMap = useRef({});
  const manuallyEditedMrpMap = useRef({});
  const percentageRateMap = useRef({});

  const handleSave = useCallback(() => {
    const payload = selectedRows
      .map((id) => {
        let originalRow = testRateData?.data?.data.find((row) => row.id === id);
        if (!originalRow) {
          originalRow = mergedData.find((row) => row.id === id);
        }

        const finalRate = localRateMap.current[id]
          ? Math.ceil(Number(localRateMap.current[id]))
          : Number(originalRow.rate);

        const finalMrpRate = localRateMrpMap.current[id]
          ? Math.ceil(Number(localRateMrpMap.current[id]))
          : Number(originalRow.mrp_rate);

        console.log("finalRate", finalRate);
        console.log("finalMrpRate", finalMrpRate);

        if (originalRow && !originalRow.error) {
          return {
            id: Number(originalRow.id),
            test_code: originalRow.test_code,
            created_by: Number(originalRow.created_by),
            is_active: Number(originalRow.is_active),
            investigation_id: Number(originalRow.investigation_id),
            test_name: originalRow.test_name,
            rate: finalRate,
            mrp_rate: finalMrpRate,
            zone_id: Number(originalRow.zone_id || zoneLocalStorageId()),
            franchise_id: Number(originalRow.franchise_id),
            status: "PENDING",
            transfer_all: transferRateToAllFranchise,
          };
        }
        return null;
      })
      .filter((row) => row !== null);
    if (payload?.length > 0) {
      // FIRST disable sorting
      setEnableSorting(false);

      // THEN reset all states
      setRate("");
      setMrp("");
      setRatePercentage("");
      setMrpPercentage("");
      setEditableRows({});
      setSelectedRows([]);
      setTransferRateToAllFranchise(false);

      // Reset previousData to original data
      setPreviousData([]);

      // Call API
      updatePanelMasterMutate({ bulk_update_test_rate: payload });
    }
  }, [selectedRows, testRateData?.data?.data, updatePanelMasterMutate]);

  const [localRateEditableMap, setLocalRateEditableMap] = useState({});

  const columns = useMemo(() => {
    return TEST_RATE_COLUMNS_DATA({
      localRateMap,
      localRateMrpMap,
      manuallyEditedMap,
      manuallyEditedMrpMap,
      percentageRateMap,
      filters,
      toggleSelectAll,
      isEditable,
      selectedRows,
      editableRows,
      setEditableRows,
      toggleSelectRow,
      mrp,
      rate,
      mrpPercentage,
      ratePercentage,
      data: testRateData?.data?.data || [],
      isSuperFranchise,
      isFranchise,
      isSubFranchise,
      hasAppliedPercentageMap,
      // error,
      setError,
      localRateEditableMap,
      setLocalRateEditableMap,
      mergedData,
    });
  }, [
    filters,
    selectedRows,
    testRateData,
    isEditable,
    editableRows,
    mrp,
    rate,
    mrpPercentage,
    ratePercentage,
    isSuperFranchise,
    isFranchise,
    isSubFranchise,
    hasAppliedPercentageMap,
    setError,
    localRateEditableMap,
    percentageRateMap,
    mergedData,
  ]);

  const columnsInvestigationManager = useMemo(() => {
    return INVESTIGATION_MANAGER_COLUMNS_DATA({
      // deleteRow,
      // showObservation,
      // editInvestigationManager,
      filters,
      // isSuperFranchise,
      // isFranchiseEmployee,
      // isSubFranchise,
      // showInvestigationComment,
      // showInvestigationInterpretation,
      // showHelpMenu,
      // openAddDelivaryDays,
      // openAddSequence,
      // openAddInvestigationNabl,
      // openAddInvestigationObservationFormula,
      // openAddInvestigationTemplate,
      // ShowInvestigationSampleReport,
    });
  }, [
    filters.is_active,
    // deleteRow,
    // showObservation,
    // editInvestigationManager,
    // showInvestigationComment,
    // showInvestigationInterpretation,
    // showHelpMenu,
    // openAddDelivaryDays,
    // openAddSequence,
    // openAddInvestigationNabl,
    // openAddInvestigationObservationFormula,
    // openAddInvestigationTemplate,
    // isSuperFranchise,
    // isFranchiseEmployee,
    // isSubFranchise,
    // ShowInvestigationSampleReport,
  ]);

  useEffect(() => {
    // console.log("third useEffect");

    if (
      !isFetching && // ← YE CHANGE KIYA HAI (isFetchingData ki jagah isFetching)
      testRateData?.data?.additional_data?.is_editable !== undefined
    ) {
      setIsEditable(testRateData.data.additional_data.is_editable);
    }
  }, [isFetching, testRateData?.data?.additional_data?.is_editable]);

  const isFranchiseEmployee = localStorage.getItem("is_franchise_employee");

  const exportToCSV = () => {
    if (!mergedData || mergedData.length === 0) {
      alert("No data to export");
      return;
    }

    let filteredColumns = columns.filter((col) => col.accessor !== "_select");

    if (isFranchiseEmployee === "1") {
      filteredColumns = filteredColumns.filter(
        (col) => col.accessor !== "rates"
      );
    }

    const headers = filteredColumns
      .map((col) => col.Header || col.accessor)
      .join(",");

    const csvContent = mergedData
      .map((row) =>
        filteredColumns
          .map((col) => {
            const value = row[col.accessor] || "";
            return typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(",")
      )
      .join("\n");

    const fullCSV = headers + "\n" + csvContent;

    const blob = new Blob([fullCSV], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `table-data-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!mergedData || mergedData.length === 0) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(12);

    const lines = [
      "LIMS SPECIALITY LABORATORY PVT. LTD.",
      "Head Office : 12, Indra Biswas Road (Near Belgachia Tricon Park)",
      "Kolkata - 700037 (Near - Belgachia Post Office)",
      "Branch Office : Guru Govind Singh link path road, Dist. - Patna,",
      "P.O. - Azimabad, Bihar - 800009, (Opposite of Soalty Hotel,",
      "Champaran Meet shop 2nd floor)",
    ];

    let startY = 10;

    lines.forEach((line, index) => {
      const textWidth = doc.getTextWidth(line);
      doc.text(line, (pageWidth - textWidth) / 2, startY + index * 6);
    });

    const contentStartY = startY + lines.length * 6 + 5;

    let filteredColumns = columns.filter((col) => col.accessor !== "_select");

    if (isFranchiseEmployee === "1") {
      filteredColumns = filteredColumns.filter(
        (col) => col.accessor !== "rates"
      );
    }

    const tableColumn = filteredColumns.map(
      (col) => col.Header || col.accessor
    );
    const tableRows = mergedData.map((row) =>
      filteredColumns.map((col) => row[col.accessor] ?? "-")
    );

    autoTable(doc, {
      startY: contentStartY,
      head: [tableColumn],
      body: tableRows,
      styles: {
        fontSize: 8,
        cellWidth: "wrap",
      },
      headStyles: {
        fillColor: [0, 112, 192],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [249, 249, 249] },
      margin: { left: 10, right: 10 },
      theme: "grid",
    });

    doc.save(`table-data-${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const getRowProps = useCallback(
    (row) => ({
      onClick: () => {
        console.log("Row clicked:", row);
        const franchiseId = localStorage.getItem("franchise_id");
        setSelectedFranchiseId(franchiseId);
        setSelectedInvestigationId(row.id);

        setTimeout(() => {
          setIsAddModalOpen(true);
        }, 100);
      },
    }),
    []
  );

  const {
    data: investigationManagerData,
    // isLoading,
    refetch: refetchInvestigationManager,
  } = useQuery(
    ["fetch-InvestigationManager", currentPage],
    () => fetchInvestigationManager(currentPage, filters),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res.data.additional_data.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;

        const formatDate = (dateString) => {
          const [year, month, day] = dateString.split("-");
          return `${day}-${month}-${year}`;
        };

        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
            // auto_consume: x.auto_consume ? formatDate(x.auto_consume) : null,
            // billing_category: x.billing_category
            //   ? formatDate(x.billing_category)
            //   : null,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching Investigation/Manager List:", res);
      },
    }
  );

  const loadNextPage = useCallback(() => {
    updateCurrentPage(currentPage + 1);
  }, [currentPage, updateCurrentPage]);

  const loadPreviousPage = useCallback(() => {
    updateCurrentPage(currentPage - 1);
  }, [currentPage, updateCurrentPage]);

  const loadFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      updateCurrentPage(1);
    }
  }, [currentPage, updateCurrentPage]);

  const loadLastPage = useCallback(() => {
    if (currentPage !== totalPages) {
      updateCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, updateCurrentPage]);

  return (
    <div className="container-fluid mb-3">
      {isSaving && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Loader isLoading={true} />
          </div>
        </div>
      )}
      <div className="box mb-4">
        <div className="boxHeader d-flex justify-content-between align-items-center">
          <h1>Franchise Investigation Details</h1>
          {/* {
            <div className="d-flex align-items-center">
              {testRateData?.data?.data?.length > 6 &&
                !isFetchingData &&
                shouldShowTable && (
                  <button
                    type="submit"
                    onClick={handleSave}
                    // disabled={error}
                    disabled={error || isSaving}
                    className="btn btn-primary me-2"
                  >
                    Save
                  </button>
                )}
              <div className="d-flex gap-2 me-4">
                <button
                  onClick={exportToCSV}
                  className="btn btn-success bg-green-600 hover:bg-green-700"
                >
                  Export CSV
                </button>
                <button
                  onClick={exportToPDF}
                  className="btn btn-danger bg-red-600 hover:bg-red-700"
                >
                  Export PDF
                </button>
              </div>
              {isSuperFranchise != 1 &&
                isSubFranchise != 1 &&
                isFranchise != 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      refetchTestRate();
                      updatePanelMasterSync();
                    }}
                    className="btn btn-outline-primary d-flex align-items-center"
                    title="Refresh"
                  >
                    <FaSyncAlt />
                  </button>
                )}
            </div>
          } */}
        </div>

        <Modal
          type="modal-lg"
          show={isAddModalOpen}
          onHide={() => setIsAddModalOpen(false)}
          modalName={MODALS.ADD_AREA_MODAL}
          body={
            <Details
              item={franchiseInvestigationDetailsData?.data}
              onClose={() => setIsAddModalOpen(false)}
            />
          }
        />

        <div className="boxBody">
          <div className="left d-lg-flex flex-wrap align-items-center">
            <FilterPanelMaster
              updateFilter={updateFilter}
              filters={filters}
              updateMultipleFilters={updateMultipleFilters}
              mrp={mrp}
              rate={rate}
              ratePercentage={ratePercentage}
              mrpPercentage={mrpPercentage}
              setMrp={setMrp}
              setRate={setRate}
              setRatePercentage={setRatePercentage}
              setMrpPercentage={setMrpPercentage}
              isSuperFranchise={isSuperFranchise}
              isFranchise={isFranchise}
              isSubFranchise={isSubFranchise}
              transferRateToAllFranchise={transferRateToAllFranchise}
              setTransferRateToAllFranchise={setTransferRateToAllFranchise}
              editableRows={editableRows}
              hasAppliedPercentageMap={hasAppliedPercentageMap}
            />
          </div>
          <hr />
          {/* {!isFetchingData && shouldShowTable && (
            <>
              <div className="mt-3">
                <Table
                  data={mergedData || []}
                  isLoading={isFetchingData}
                  tableColumns={columns}
                  showPagination={false}
                  getRowProps={getRowProps}
                />
              </div>
              <div>
                <button
                  type="submit"
                  onClick={handleSave}
                  // disabled={error}
                  disabled={error || isSaving}
                  className="btn btn-primary mt-4"
                >
                  Save
                </button>
              </div>
            </>
          )} */}
          <Table
            data={investigationManagerData?.data?.data || []}
            isLoading={isLoading}
            tableColumns={columnsInvestigationManager}
            currentPage={currentPage}
            totalPages={totalPages}
            itemPerPage={itemPerPage}
            disablePrev={disablePrev}
            disableNext={disableNext}
            disableFirst={disableFirst}
            disableLast={disableLast}
            loadFirstPage={loadFirstPage}
            loadLastPage={loadLastPage}
            totalRecords={5}
            loadNextPage={loadNextPage}
            loadPreviousPage={loadPreviousPage}
            getRowProps={getRowProps}
          />
          {/* {isFetchingData && <Loader isLoading={true} />} */}
        </div>
      </div>
    </div>
  );
};

export default PanelMaster;
